import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Target,
  Calendar,
  DollarSign,
  Users,
  CheckCircle,
  AlertTriangle,
  Clock,
  Activity,
  Award,
  FileText,
  GitBranch,
  Shield,
  Brain,
  BarChart3,
  AlertCircle,
  Network,
  MessageCircle,
  XCircle,
  RotateCcw,
} from 'lucide-react'
import { projectsService, Project } from '../services/projects'
import { calculateBudgetVariance, formatBudgetVariance } from '../utils/apiTransformers'
import { tasksService, Task, TaskStatus } from '../services/tasks'
import { toast } from 'sonner'
import { ResourceGate } from '../components/common/ResourceGate'
import { ApprovalGatesSection } from '../components/project/ApprovalGatesSection'
import { ScopeSection } from '../components/project/ScopeSection'
import { PredictionCard } from '../components/ui/PredictionCard'
import { WBSSection } from '../components/project/WBSSection'
import { TimelineSection } from '../components/project/TimelineSection'
import { SIMPLE_MODE_TABS, useWorkspaceMode } from '../services/workspaceMode'
import { ProjectRAIDSection } from '../components/project/ProjectRAIDSection'
import { DecisionsSection } from '../components/project/DecisionsSection'
import { BudgetManagement } from '../components/project/BudgetManagement'
import { ActionsSection } from '../components/project/ActionsSection'
import { ProjectOrgSection } from '../components/project/ProjectOrgSection'
import { CommunicationPlanSection } from '../components/project/CommunicationPlanSection'
import { ProjectQASection } from '../components/project/ProjectQASection'
import { DocumentsSection } from '../components/project/DocumentsSection'
import { GovernanceSection } from '../components/project/GovernanceSection'
import { AIAssistantSection } from '../components/project/AIAssistantSection'
import { ReportsSection } from '../components/project/ReportsSection'
import { ShareLinksManager } from '../components/ShareLinksManager'
import { CustomFieldsManager } from '../components/project/CustomFieldsManager'
import { TaskStatusesManager } from '../components/project/TaskStatusesManager'
import { EvidenceRequirementsManager } from '../components/project/EvidenceRequirementsManager'
import { Link2, SlidersHorizontal, MessagesSquare } from 'lucide-react'
import { aiInsightsService, AIInsight } from '../services/aiInsights'
import { InsightCard } from '../components/ui/InsightCard'
import { CommentThread } from '../components/CommentThread'
import { ActivityFeed } from '../components/ActivityFeed'
import { AttachmentList } from '../components/AttachmentList'
import { GuestInviteModal } from '../components/GuestInviteModal'
import { PermissionsSection } from '../components/project/PermissionsSection'
import { Lock, CalendarRange } from 'lucide-react'
import { apiErrorMessage, type ApiErrorLike } from '../utils/apiError'

const TAB_IDS = [
  'dashboard', 'gates', 'scope', 'wbs', 'timeline', 'raid', 'decisions', 'budget', 'tasks',
  'actions', 'project-org', 'project-comms', 'qa', 'documents', 'governance', 'ai', 'reports', 'sharing',
  'custom-fields', 'collab', 'guests', 'permissions', 'audit',
] as const

function isValidTab(tab: string | undefined): tab is (typeof TAB_IDS)[number] {
  return !!tab && TAB_IDS.includes(tab as (typeof TAB_IDS)[number])
}

export default function ProjectWorkspace() {
  const { id, tab: tabParam } = useParams<{ id: string; tab?: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<Project | null>(null)
  const [statusBusy, setStatusBusy] = useState(false)
  const changeStatus = async (status: 'active' | 'cancelled', done: string) => {
    if (!project) return
    setStatusBusy(true)
    try {
      setProject(await projectsService.update(project.id, { status }))
      toast.success(done)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not change the project status.'))
    } finally {
      setStatusBusy(false)
    }
  }
  const [loading, setLoading] = useState(true)
  const tabFromUrl = isValidTab(tabParam) ? tabParam : 'dashboard'
  const [activeTab, setActiveTab] = useState(tabFromUrl)
  const [tasks, setTasks] = useState<Task[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)
  const [dashboardInsights, setDashboardInsights] = useState<AIInsight[]>([])

  // Sync activeTab from URL (e.g. browser back/forward or direct link)
  useEffect(() => {
    setActiveTab(tabFromUrl)
  }, [tabFromUrl])

  useEffect(() => {
    if (id) {
      fetchProject()
    }
  }, [id])

  useEffect(() => {
    if (id && activeTab === 'tasks') {
      fetchTasks()
    }
  }, [id, activeTab])

  const fetchProject = async () => {
    setLoading(true)
    try {
      const [data, insightData] = await Promise.all([
        projectsService.get(parseInt(id!)),
        aiInsightsService.list({ entity_type: 'project', entity_id: parseInt(id!), limit: 2 }).catch(() => []),
      ])
      setProject(data)
      setDashboardInsights(insightData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching project:', error)
      toast.error('Failed to load project')
      navigate('/project')
    } finally {
      setLoading(false)
    }
  }

  const fetchTasks = async () => {
    if (!id) return
    setTasksLoading(true)
    try {
      const data = await tasksService.list({ project_id: parseInt(id) })
      setTasks(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching tasks:', error)
      toast.error('Failed to load tasks')
    } finally {
      setTasksLoading(false)
    }
  }

  const handleTabClick = (tabId: string) => {
    if (!id) return
    if (tabId === 'dashboard') {
      navigate(`/project/${id}`, { replace: true })
    } else {
      navigate(`/project/${id}/${tabId}`, { replace: true })
    }
  }

  // IA — consolidated from 22 flat sidebar items → 6 top-level groups, each
  // rendering a sub-tab strip above its content. URLs stay `/:id/:childId` so
  // bookmarks, AI insight nav targets, and direct links still resolve.
  const [workspaceMode, setWorkspaceMode] = useWorkspaceMode()
  const allTabGroups: Array<{
    id: string
    label: string
    icon: typeof Target
    children: Array<{ id: typeof TAB_IDS[number]; label: string; icon: typeof Target }>
  }> = [
    {
      id: 'overview',
      label: 'Overview',
      icon: Target,
      children: [
        { id: 'dashboard', label: 'Dashboard', icon: Target },
        { id: 'tasks', label: 'Tasks', icon: CheckCircle },
      ],
    },
    {
      id: 'plan',
      label: 'Plan',
      icon: GitBranch,
      children: [
        { id: 'scope', label: 'Scope', icon: Target },
        { id: 'wbs', label: 'Work Breakdown', icon: GitBranch },
        { id: 'timeline', label: 'Timeline', icon: CalendarRange },
        { id: 'gates', label: 'Approval Gates', icon: CheckCircle },
        { id: 'project-org', label: 'Project Org', icon: Network },
      ],
    },
    {
      id: 'execute',
      label: 'Execute',
      icon: Activity,
      children: [
        { id: 'actions', label: 'Action Items', icon: Clock },
        { id: 'decisions', label: 'Decisions', icon: CheckCircle },
      ],
    },
    {
      id: 'monitor',
      label: 'Monitor',
      icon: Shield,
      children: [
        { id: 'raid', label: 'RAID', icon: AlertTriangle },
        { id: 'budget', label: 'Budget', icon: DollarSign },
        { id: 'qa', label: 'Quality', icon: Award },
        { id: 'governance', label: 'Governance', icon: Shield },
      ],
    },
    {
      id: 'communicate',
      label: 'Communicate',
      icon: MessageCircle,
      children: [
        { id: 'project-comms', label: 'Comms Plan', icon: MessageCircle },
        { id: 'documents', label: 'Documents', icon: FileText },
        { id: 'reports', label: 'Reports', icon: BarChart3 },
        { id: 'collab', label: 'Collaboration', icon: MessagesSquare },
        { id: 'ai', label: 'AI Assistant', icon: Brain },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Lock,
      children: [
        { id: 'sharing', label: 'Sharing', icon: Link2 },
        { id: 'custom-fields', label: 'Custom Fields', icon: SlidersHorizontal },
        { id: 'guests', label: 'Guests', icon: Users },
        { id: 'permissions', label: 'Permissions', icon: Lock },
        { id: 'audit', label: 'Audit', icon: Activity },
      ],
    },
  ]

  // Resolve the current legacy tab id → its parent group. Lets us highlight
  // the correct top-level button and render the right sub-tab strip without
  // changing the URL contract.
  // Simple mode (new self-serve orgs) hides the PMO toolset until an admin
  // turns it on. A deep link to a hidden tab still opens that tab.
  const tabGroups = workspaceMode.mode === 'simple'
    ? allTabGroups
        .map((g) => ({ ...g, children: g.children.filter((c) => SIMPLE_MODE_TABS.has(c.id) || c.id === activeTab) }))
        .filter((g) => g.children.length > 0)
    : allTabGroups
  const activeGroup = tabGroups.find((g) => g.children.some((c) => c.id === activeTab)) ?? tabGroups[0]
  const activeChildren = activeGroup.children

  const formatDate = (date: string | null) => {
    if (!date) return 'Not set'
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatCurrency = (amount: string | null) => {
    if (!amount) return '$0.00'
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const getStatusColor = (status: string | TaskStatus) => {
    switch (status) {
      // Project statuses
      case 'active':
        return 'bg-green-100 text-green-700 border-green-300'
      case 'planning':
        return 'bg-blue-100 text-blue-700 border-blue-300'
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'completed':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-300'
      // Task statuses
      case 'todo':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-300'
      case 'in_review':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'done':
        return 'bg-green-100 text-green-700 border-green-300'
      case 'blocked':
        return 'bg-red-100 text-red-700 border-red-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-700 border-red-300'
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'low':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4"></div>
          <p className="text-[#486581]">Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-[#102a43]">Project not found</p>
          <button
            onClick={() => navigate('/project')}
            className="mt-4 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692]"
          >
            Back to Projects
          </button>
        </div>
      </div>
    )
  }

  const budget = parseFloat(project.budget || '0')
  const actualCost = parseFloat(project.actual_cost || '0')
  // KQA-9 — null when no budget or no spend yet (renders "—", never "-100%").
  const budgetVariance = calculateBudgetVariance(project.budget, project.actual_cost)

  return (
    <div className="flex h-screen bg-[#f0f4f8]">
      {/* Left Sidebar Navigation */}
      <div className="w-64 bg-[#486581] border-r border-[#5C748E] overflow-y-auto flex-shrink-0">
        {/* Back Button */}
        <button
          onClick={() => navigate('/project')}
          className="w-full flex items-center gap-2 px-4 py-3 text-[#C5CDD6] hover:bg-[#3D4F61] border-b border-[#3D4F61]"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Projects</span>
        </button>

        {/* Project Info */}
        <div className="p-4 border-b border-[#3D4F61]">
          <h2 className="font-medium mb-1 text-white text-sm truncate">{project.name}</h2>
          <p className="text-xs text-[#A3B1BF] mb-3">ID: {project.id}</p>
          <div className="flex items-center gap-2 mb-3">
            <div
              className={`w-2 h-2 rounded-full ${
                project.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
              }`}
            ></div>
            <span className="text-xs text-green-400 capitalize">{project.status}</span>
          </div>
          
          {/* Cancel / reopen project (2026-09-29: was a "coming soon" toast) */}
          {project.status === 'cancelled' ? (
            <button
              onClick={() => changeStatus('active', 'Project reopened.')}
              disabled={statusBusy}
              className="w-full px-3 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-sm bg-white/10 text-white border border-white/20 hover:bg-white/20 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              Reopen Project
            </button>
          ) : project.status !== 'completed' && (
            <button
              onClick={() => {
                if (confirm('Cancel this project? It stays in your records as cancelled and drops out of active reporting. You can reopen it later.')) {
                  changeStatus('cancelled', 'Project cancelled.')
                }
              }}
              disabled={statusBusy}
              className="w-full px-3 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-sm bg-red-600/20 text-red-300 border border-red-500/30 hover:bg-red-600/30 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              Cancel Project
            </button>
          )}
        </div>

        {/* Tabs — 6 top-level groups. Clicking a group jumps to its first
            child; the sub-tab strip above the content lets the user swap
            within the group without leaving the sidebar context. */}
        <div className="p-2">
          {tabGroups.map((group) => {
            const Icon = group.icon
            const firstChild = group.children[0].id
            const isActive = activeGroup.id === group.id
            return (
              <button
                key={group.id}
                onClick={() => handleTabClick(firstChild)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? 'bg-[#DAA520] text-white'
                    : 'text-[#C5CDD6] hover:bg-[#3D4F61] hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{group.label}</span>
              </button>
            )
          })}
          {workspaceMode.can_change && (
            <button
              type="button"
              onClick={() =>
                setWorkspaceMode(workspaceMode.mode === 'simple' ? 'pmo' : 'simple')
                  .then(() => toast.success(workspaceMode.mode === 'simple' ? 'PMO tools turned on for your organization' : 'Switched to simple mode'))
                  .catch(() => toast.error('Could not change the workspace mode.'))
              }
              className="mt-3 w-full rounded-lg border border-[#3D4F61] px-3 py-2 text-left text-xs text-[#C5CDD6] hover:bg-[#3D4F61] hover:text-white"
            >
              {workspaceMode.mode === 'simple'
                ? 'Turn on PMO tools (RAID, gates, governance, budget)'
                : 'Switch to simple mode'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Sub-tab strip — shown when the active group has more than one
            child. Skipped for single-child groups to avoid a meaningless
            row. */}
        {activeChildren.length > 1 && (
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6">
            <div className="flex gap-1 overflow-x-auto">
              {activeChildren.map((child) => {
                const ChildIcon = child.icon
                const childActive = activeTab === child.id
                return (
                  <button
                    key={child.id}
                    onClick={() => handleTabClick(child.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ${
                      childActive
                        ? 'border-[#DAA520] text-[#102a43] font-medium'
                        : 'border-transparent text-[#6F8394] hover:text-[#102a43] hover:border-gray-300'
                    }`}
                  >
                    <ChildIcon className="w-4 h-4" />
                    {child.label}
                  </button>
                )
              })}
            </div>
          </div>
        )}
        {activeTab === 'dashboard' && (
          <div className="p-6 space-y-6">
            {/* AI Insights Banner */}
            {dashboardInsights.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-[#DAA520]" />
                  <span className="text-sm font-semibold text-[#2A3B4C]">AI Insights</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {dashboardInsights.map(i => (
                    <InsightCard key={i.id} insight={i} onFeedback={(iid, score) => aiInsightsService.submitFeedback(iid, score)} />
                  ))}
                </div>
              </section>
            )}
            {/* Project Header */}
            <div className="mb-6">
              <div className="mb-4">
                <h1 className="text-3xl font-semibold text-[#2A3B4C] mb-2">{project.name}</h1>
                <p className="text-lg text-[#556B7C]">{project.description || 'No description available'}</p>
              </div>
              <div className="mb-4">
                <PredictionCard entityType="project" entityId={project.id} />
              </div>
              {/* Stats Cards */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Start Date', value: formatDate(project.start_date) },
                  { label: 'End Date', value: formatDate(project.end_date) },
                  { label: 'Budget', value: formatCurrency(project.budget) },
                  { label: 'Progress', value: `${project.progress}%` },
                ].map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                    <div className="text-sm text-[#6F8394] mb-2">{stat.label}</div>
                    <div className="text-xl font-semibold text-[#2A3B4C]">{stat.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
                <h3 className="text-sm font-medium text-[#829ab1] mb-2">Status</h3>
                <div className={`inline-block px-3 py-1 rounded-lg border-2 capitalize ${getStatusColor(project.status)}`}>
                  {project.status.replace('_', ' ')}
                </div>
              </div>
              <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
                <h3 className="text-sm font-medium text-[#829ab1] mb-2">Priority</h3>
                <div className={`inline-block px-3 py-1 rounded-lg border-2 capitalize ${getPriorityColor(project.priority)}`}>
                  {project.priority}
                </div>
              </div>
            </div>

            {/* Health Metrics Grid */}
            <div>
              <h2 className="text-lg font-semibold text-[#102a43] mb-4">Project Health Scorecard</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-white p-5 rounded-lg border-2 border-[#bcccdc] shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-[#829ab1]">Overall</div>
                    <Award className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-3xl mb-2 font-semibold text-[#102a43]">{project.progress}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border-2 border-[#bcccdc] shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-[#829ab1]">Schedule</div>
                    <Calendar className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div className="text-3xl mb-2 font-semibold text-[#102a43]">{project.progress}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-600 h-2 rounded-full"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border-2 border-[#bcccdc] shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-[#829ab1]">Cost</div>
                    <DollarSign className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-3xl mb-2 font-semibold text-[#102a43]">
                    {budget > 0 ? Math.round((actualCost / budget) * 100) : 0}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${(budgetVariance ?? 0) > 0 ? 'bg-red-600' : 'bg-green-600'}`}
                      style={{
                        width: `${budget > 0 ? Math.min(100, Math.abs((actualCost / budget) * 100)) : 0}%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border-2 border-[#bcccdc] shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-[#829ab1]">Scope</div>
                    <Target className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-3xl mb-2 font-semibold text-[#102a43]">{project.progress}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border-2 border-[#bcccdc] shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-[#829ab1]">Quality</div>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-3xl mb-2 font-semibold text-[#102a43]">{project.progress}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg border-2 border-[#bcccdc] shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-[#829ab1]">Team</div>
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-3xl mb-2 font-semibold text-[#102a43]">{project.progress}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Information */}
            {project.budget && (
              <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
                <h2 className="text-lg font-semibold text-[#102a43] mb-4">Budget Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-[#829ab1] mb-1">Budget</div>
                    <div className="text-2xl font-semibold text-[#102a43]">{formatCurrency(project.budget)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-[#829ab1] mb-1">Actual Cost</div>
                    <div className="text-2xl font-semibold text-[#102a43]">{formatCurrency(project.actual_cost)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-[#829ab1] mb-1">Variance</div>
                    <div
                      className={`text-2xl font-semibold ${
                        budgetVariance === null
                          ? 'text-[#102a43]'
                          : budgetVariance > 0
                          ? 'text-red-600'
                          : budgetVariance < 0
                          ? 'text-green-600'
                          : 'text-[#102a43]'
                      }`}
                    >
                      {formatBudgetVariance(project.budget, project.actual_cost)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
              <h2 className="text-lg font-semibold text-[#102a43] mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => handleTabClick('tasks')}
                  className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left"
                >
                  <CheckCircle className="w-6 h-6 text-[#829ab1] mb-2" />
                  <div className="font-medium text-[#102a43] text-sm">View Tasks</div>
                </button>
                <button
                  onClick={() => handleTabClick('budget')}
                  className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left"
                >
                  <DollarSign className="w-6 h-6 text-[#829ab1] mb-2" />
                  <div className="font-medium text-[#102a43] text-sm">Budget Details</div>
                </button>
                <button
                  onClick={() => handleTabClick('dashboard')}
                  className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left"
                >
                  <Calendar className="w-6 h-6 text-[#829ab1] mb-2" />
                  <div className="font-medium text-[#102a43] text-sm">View Timeline</div>
                </button>
                <ResourceGate
                  entityType="project"
                  entityId={id ? parseInt(id, 10) : null}
                  minRole="contributor"
                >
                  <button
                    onClick={() => navigate(`/project/${id}/edit`)}
                    className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left"
                  >
                    <FileText className="w-6 h-6 text-[#829ab1] mb-2" />
                    <div className="font-medium text-[#102a43] text-sm">Edit Project</div>
                  </button>
                </ResourceGate>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'gates' && (
          <ApprovalGatesSection projectId={id ? parseInt(id, 10) : undefined} />
        )}

        {activeTab === 'scope' && (
          <ScopeSection projectId={id ? parseInt(id, 10) : undefined} />
        )}

        {activeTab === 'timeline' && (
          <TimelineSection projectId={id ? parseInt(id, 10) : undefined} />
        )}
        {activeTab === 'wbs' && (
          <WBSSection projectId={id ? parseInt(id, 10) : undefined} />
        )}

        {activeTab === 'raid' && <ProjectRAIDSection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'decisions' && <DecisionsSection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'actions' && <ActionsSection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'project-org' && <ProjectOrgSection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'project-comms' && <CommunicationPlanSection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'qa' && <ProjectQASection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'documents' && <DocumentsSection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'governance' && <GovernanceSection projectId={id ? parseInt(id, 10) : undefined} />}

        {activeTab === 'ai' && (
          <AIAssistantSection
            projectId={id ? parseInt(id, 10) : undefined}
            programId={project?.program_id ?? undefined}
          />
        )}

        {activeTab === 'reports' && (
          <ReportsSection
            projectId={id ? parseInt(id, 10) : undefined}
            projectCode={project?.name}
          />
        )}

        {activeTab === 'sharing' && id && (
          <div className="p-6 max-w-3xl">
            <ShareLinksManager projectId={parseInt(id, 10)} />
          </div>
        )}

        {activeTab === 'custom-fields' && id && (
          <div className="p-6 max-w-3xl space-y-10">
            <CustomFieldsManager organizationId={project.organization_id ?? null} entityType="task" />
            <hr className="border-border" />
            <TaskStatusesManager projectId={parseInt(id, 10)} />
            <hr className="border-border" />
            <EvidenceRequirementsManager projectId={parseInt(id, 10)} />
          </div>
        )}

        {activeTab === 'collab' && id && (
          <div className="p-4 sm:p-6 max-w-5xl grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <section className="bg-white rounded-lg border-2 border-[#bcccdc] p-4">
                <h2 className="text-base font-semibold text-[#102a43] mb-3">Files</h2>
                <AttachmentList entityType="project" entityId={parseInt(id, 10)} />
              </section>
              <section className="bg-white rounded-lg border-2 border-[#bcccdc] p-4">
                <h2 className="text-base font-semibold text-[#102a43] mb-3">Comments</h2>
                <CommentThread entityType="project" entityId={parseInt(id, 10)} />
              </section>
            </div>
            <aside>
              <section className="bg-white rounded-lg border-2 border-[#bcccdc] p-4 sticky top-4">
                <h2 className="text-base font-semibold text-[#102a43] mb-3">Activity</h2>
                <ActivityFeed entityType="project" entityId={parseInt(id, 10)} limit={50} />
              </section>
            </aside>
          </div>
        )}

        {activeTab === 'guests' && id && (
          <ProjectGuestsTab projectId={parseInt(id, 10)} />
        )}

        {activeTab === 'permissions' && id && (
          <PermissionsSection projectId={parseInt(id, 10)} />
        )}

        {activeTab === 'audit' && id && (
          <div className="p-4 sm:p-6 max-w-4xl">
            <header className="mb-4">
              <h2 className="text-lg font-semibold text-[#102a43]">Project audit log</h2>
              <p className="text-sm text-[#486581] mt-1">
                Who viewed, edited, and changed access to this project.
              </p>
            </header>
            <section className="bg-white rounded-lg border-2 border-[#bcccdc] p-4">
              <ActivityFeed entityType="project" entityId={parseInt(id, 10)} limit={200} />
            </section>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="p-6 space-y-6">
            <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#102a43]">Project Tasks</h2>
                <button
                  onClick={() => navigate(`/task/create?project_id=${id}`)}
                  className="px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Create Task
                </button>
              </div>

              {tasksLoading ? (
                <div className="text-center py-8 text-[#829ab1]">Loading tasks...</div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-8 text-[#829ab1]">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-[#bcccdc]" />
                  <p>No tasks found for this project.</p>
                  <button
                    onClick={() => navigate(`/task/create?project_id=${id}`)}
                    className="mt-4 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors"
                  >
                    Create First Task
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#bcccdc]">
                    <thead className="bg-[#f0f4f8]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Estimated Hours
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-[#829ab1] uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#bcccdc]">
                      {tasks.map((task) => (
                        <tr key={task.id} className="hover:bg-[#f0f4f8] transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-[#102a43]">{task.title}</div>
                            {task.description && (
                              <div className="text-sm text-[#829ab1] truncate max-w-md">{task.description}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(
                                task.status
                              )}`}
                            >
                              {task.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full border ${getPriorityColor(
                                task.priority
                              )}`}
                            >
                              {task.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#829ab1]">
                            {task.due_date ? formatDate(task.due_date) : 'Not set'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#829ab1]">
                            {task.estimated_hours ? `${task.estimated_hours}h` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => navigate(`/task/${task.id}`)}
                              className="text-[#486581] hover:text-[#102a43] transition-colors"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'budget' && (
          <div className="p-6">
            <BudgetManagement
              projectId={id ? parseInt(id, 10) : undefined}
              projectCode={project?.name || (id ? `Project ${id}` : 'Project')}
            />
          </div>
        )}

      </div>
    </div>
  )
}


function ProjectGuestsTab({ projectId }: { projectId: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="p-4 sm:p-6 max-w-3xl">
      <div className="bg-white rounded-lg border-2 border-[#bcccdc] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-base font-semibold text-[#102a43]">Guest collaborators</h2>
            <p className="text-sm text-[#556B7C]">
              Invite clients, inspectors, or vendors to view this project. Scoped, token-based, time-limited.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="min-h-[44px] rounded-md bg-[#486581] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            + Invite guest
          </button>
        </div>
        <p className="text-xs text-[#556B7C]">
          Existing invites appear in the modal once you open it.
        </p>
      </div>
      <GuestInviteModal
        open={open}
        onClose={() => setOpen(false)}
        scope="project"
        scopeId={projectId}
      />
    </div>
  )
}
